"use client";
import { KeyboardEvent, ReactNode, useId, useRef, useState } from "react";

interface TabItem {
  title: string;
  content: ReactNode;
}

interface CustomTabsProps {
  tabs: TabItem[];
  onTabChange?: (index: number) => void;
  defaultActiveTab?: number;
  ariaLabel?: string;
}

const getSafeTabIndex = (index: number, tabsLength: number) => {
  if (tabsLength <= 0) return 0;
  return Math.min(Math.max(index, 0), tabsLength - 1);
};

export default function CustomTabs({
  tabs,
  onTabChange,
  defaultActiveTab = 0,
  ariaLabel = "Selecionar região do dashboard",
}: CustomTabsProps) {
  const [activeTab, setActiveTab] = useState(() =>
    getSafeTabIndex(defaultActiveTab, tabs.length)
  );
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const generatedId = useId().replace(/:/g, "");
  const panelId = `iframe-tabs-${generatedId}-panel`;
  const getTabId = (index: number) => `iframe-tabs-${generatedId}-tab-${index}`;

  const activateTab = (index: number, shouldFocus = false) => {
    const nextIndex = getSafeTabIndex(index, tabs.length);

    setActiveTab(nextIndex);
    onTabChange?.(nextIndex);

    if (shouldFocus) {
      requestAnimationFrame(() => {
        tabRefs.current[nextIndex]?.focus();
      });
    }
  };

  const handleTabClick = (index: number) => {
    activateTab(index);
  };

  const handleTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (tabs.length <= 0) return;

    const lastIndex = tabs.length - 1;
    let nextIndex: number | null = null;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = index === lastIndex ? 0 : index + 1;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = index === 0 ? lastIndex : index - 1;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    activateTab(nextIndex, true);
  };

  if (tabs.length === 0) return null;

  const safeActiveTab = getSafeTabIndex(activeTab, tabs.length);

  return (
    <div className="agora-tabs desktop">
      <div role="tablist" aria-label={ariaLabel} className="tab-list desktop">
        {tabs.map((tab, index) => (
          <button
            key={index}
            id={getTabId(index)}
            type="button"
            role="tab"
            tabIndex={safeActiveTab === index ? 0 : -1}
            aria-selected={safeActiveTab === index}
            aria-controls={panelId}
            className={`tab-list-item current-neutral ${
              safeActiveTab === index ? "active" : ""
            }`}
            ref={(element) => {
              tabRefs.current[index] = element;
            }}
            onClick={() => handleTabClick(index)}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
          >
            {tab.title}
          </button>
        ))}
      </div>

      <div
        id={panelId}
        role="tabpanel"
        className="tab-panel bg-[#f9f9fa]"
        aria-labelledby={getTabId(safeActiveTab)}
        tabIndex={0}
      >
        <div className="tab-panel-content py-[4px]">
          {tabs[safeActiveTab]?.content}
        </div>
      </div>
    </div>
  );
}
